/**
 * Codec helpers for converting between generated proto message types and
 * the JSON the backend serves via protojson.
 *
 * The backend uses protojson.MarshalOptions{EmitUnpopulated:false}, so absent
 * fields are omitted. @bufbuild/protobuf fromJson handles this gracefully by
 * applying proto3 defaults for missing fields.
 */

import {
  type DescMessage,
  type Message,
  type MessageShape,
  create,
  fromJson,
  toJson,
  type JsonValue,
} from "@bufbuild/protobuf";

/**
 * Deserialise a JSON value (parsed from a fetch response) into a proto message.
 */
export function fromProtoJson<Desc extends DescMessage>(
  schema: Desc,
  value: JsonValue,
): MessageShape<Desc> {
  return fromJson(schema, value);
}

/**
 * Serialise a proto message to a plain JSON value suitable for a request body.
 */
export function toProtoJson<Desc extends DescMessage>(
  schema: Desc,
  message: MessageShape<Desc>,
): JsonValue {
  return toJson(schema, message);
}

/**
 * Convenience: parse a fetch Response body as protojson for the given schema.
 * Throws if the response is not OK or body parsing fails.
 */
export async function parseProtoResponse<Desc extends DescMessage>(
  schema: Desc,
  res: Response,
): Promise<MessageShape<Desc>> {
  const json = (await res.json()) as JsonValue;
  return fromProtoJson(schema, json);
}

/**
 * Create a new empty proto message (uses proto3 defaults for all fields).
 * Re-exported from @bufbuild/protobuf for convenience.
 */
export { create };

// Re-export Message type for use elsewhere
export type { Message };
